import React, { useCallback, useMemo, useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { ClockCounterClockwise, Info, Repeat, BookOpenText, Warning } from '@phosphor-icons/react'
import { Button, Icon, Tooltip, TooltipContent, TooltipTrigger } from '@shopflo/ui'
import GenericTableV2 from 'src/components/common/table/GenericTableV2'
import { ScrollMode } from 'src/components/common/table/table-types'
import { useLocale } from 'src/lib/hooks/useLocal'
import { Report, ReportListProps } from './types'
import MultipleReportDialog from './MultipleReportDialog'
import ReportRowActions from './ReportRowActions'
import ReportStatusLabel from './ReportStatusLabel'
import {
  formatLastGenerated,
  formatLastGeneratedTooltip,
  formatReportSchedule,
  formatReportType,
  getRecurringCadenceLabel,
  getReportRowState
} from './reportFormatters'

// Schedule text: the leading keyword is placeholder-light, the value (frequency word /
// date / range) is subtext-medium, and any trailing time reverts to placeholder-light.
const light = 'text-text-xs-regular text-zinc-400'
const medium = 'text-text-xs-medium text-zinc-500'

const ScheduleText: React.FC<{ schedule: string }> = ({ schedule }) => {
  // "Runs Daily 12AM" → Runs(light) Daily(medium) 12AM(light)
  if (schedule.startsWith('Runs ')) {
    const rest = schedule.slice(5)
    const spaceIndex = rest.indexOf(' ')
    const frequency = spaceIndex === -1 ? rest : rest.slice(0, spaceIndex)
    const suffix = spaceIndex === -1 ? '' : rest.slice(spaceIndex + 1)
    return (
      <span className='truncate'>
        <span className={light}>Runs </span>
        <span className={medium}>{frequency}</span>
        {suffix && <span className={light}> {suffix}</span>}
      </span>
    )
  }

  // "Covers Jul 1 – Jul 31" → Covers(light) Jul 1 – Jul 31(medium)
  if (schedule.startsWith('Covers ')) {
    return (
      <span className='truncate'>
        <span className={light}>Covers </span>
        <span className={medium}>{schedule.slice(7)}</span>
      </span>
    )
  }

  // "First run Jul 18 12AM" → First run(light) Jul 18(medium) 12AM(light)
  if (schedule.startsWith('First run ')) {
    const tokens = schedule.slice(10).split(' ')
    const date = tokens.slice(0, 2).join(' ')
    const time = tokens.slice(2).join(' ')
    return (
      <span className='truncate'>
        <span className={light}>First run </span>
        <span className={medium}>{date}</span>
        {time && <span className={light}> {time}</span>}
      </span>
    )
  }

  return <span className={`truncate ${medium}`}>{schedule}</span>
}

const ReportNameCell: React.FC<{ report: Report; onView: (report: Report) => void }> = ({ report, onView }) => {
  const state = getReportRowState(report)
  const isRecurring = report.frequencyType === 'RECURRING'
  const failedCount = report.runs.filter(run => run.status === 'FAILED').length

  // Leading icon reflects the report type (recurring vs one-time). Failure is shown via the
  // inline "Generation failed" label, not by swapping this icon.
  const renderLeadingIcon = () =>
    isRecurring ? (
      <Icon IconComponent={Repeat} size='md' className='shrink-0 text-zinc-500' iconProps={{ weight: 'duotone' }} />
    ) : (
      <Icon IconComponent={BookOpenText} size='md' className='shrink-0 text-zinc-500' iconProps={{ weight: 'duotone' }} />
    )

  // Right-side pill: warning (+ failed count) when failed, else the run-count number for recurring reports.
  const pill =
    state.status === 'failed' ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex h-[18px] min-w-8 shrink-0 items-center justify-end gap-0.5 rounded-md px-1 text-text-xs-medium text-zinc-500 ${
              isRecurring && failedCount > 0 ? 'cursor-pointer bg-zinc-100 transition-colors hover:bg-zinc-200' : ''
            }`}
            onClick={e => {
              e.stopPropagation()
              if (isRecurring && failedCount > 0) onView(report)
            }}
          >
            {isRecurring && failedCount > 0 ? failedCount : null}
            <Icon IconComponent={Warning} size='xs' className='text-amber-600' iconProps={{ weight: 'fill' }} />
          </span>
        </TooltipTrigger>
        <TooltipContent align='start'>{state.inlineLabel ?? 'Generation failed'}</TooltipContent>
      </Tooltip>
    ) : isRecurring ? (
      <span
        className='inline-flex h-[18px] min-w-6 shrink-0 cursor-pointer items-center justify-center rounded-md bg-zinc-100 px-1 text-text-xs-medium text-zinc-500 transition-colors hover:bg-zinc-200'
        onClick={e => {
          e.stopPropagation()
          onView(report)
        }}
      >
        {report.runs.length}
      </span>
    ) : null

  return (
    <div className='flex min-h-[48px] w-full items-center justify-between gap-2 px-2 py-3'>
      <div className='flex min-w-0 items-center gap-2'>
        <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-[9.6px]'>{renderLeadingIcon()}</span>
        <span className='min-w-0 text-text-sm-medium text-zinc-700 line-clamp-1 pr-2'>{report.name}</span>
        <ReportStatusLabel state={state} />
      </div>
      {/* Counts + warning pill sits at the right side of the title column. */}
      <div className='relative flex shrink-0 items-center self-stretch'>
        {/* Recurring: "View all" reveals on hover to the LEFT of the pill, with a gradient fade over the name. */}
        {isRecurring && (
          <div className='absolute bottom-0 right-full top-0 hidden items-stretch group-hover:flex'>
            <div className='w-10 bg-[linear-gradient(to_right,rgba(243,243,244,0),rgba(243,243,244,1))]' />
            <div className='flex items-center bg-[#f3f3f4] pr-2'>
              <Button
                variant='ghost'
                size='sm'
                className='whitespace-nowrap'
                onClick={e => {
                  e.stopPropagation()
                  onView(report)
                }}
              >
                View all
              </Button>
            </div>
          </div>
        )}
        {pill}
      </div>
    </div>
  )
}

const ReportList: React.FC<
  ReportListProps<Report[]> & {
    onEdit?: (report: Report) => void
    onSetupRecurring?: (report: Report) => void
    highlightedReportId?: string
    hasMore?: boolean
    onLoadMore?: () => void
    isLoadingMore?: boolean
  }
> = ({
  report,
  onPauseClick,
  onResumeClick,
  mutateReports,
  isLoading,
  onEdit,
  onSetupRecurring,
  highlightedReportId,
  hasMore,
  onLoadMore,
  isLoadingMore
}) => {
    const { t } = useLocale()
    const [openHistoryDialog, setOpenHistoryDialog] = useState(false)
    const [selectedReport, setSelectedReport] = useState<Report | null>(null)

    const openHistory = useCallback((row: Report) => {
      setSelectedReport(row)
      setOpenHistoryDialog(true)
    }, [])

    const columns: ColumnDef<Report>[] = useMemo(
      () => [
        {
          header: 'Name',
          id: 'name',
          minSize: 350,
          maxSize: 350,
          cell: ({ row }) => <ReportNameCell report={row.original} onView={openHistory} />
        },
        {
          header: 'Type',
          id: 'type',
          minSize: 150,
          maxSize: 150,
          cell: ({ row }) => (
            <div className='flex min-h-[48px] w-[150px] items-center gap-2 truncate px-3 text-text-xs-regular text-zinc-500'>
              {formatReportType(row.original.type, t)}
            </div>
          )
        },
        {
          header: 'Schedule',
          id: 'schedule',
          minSize: 150,
          maxSize: 150,
          cell: ({ row }) => {
            const schedule = formatReportSchedule(row.original)
            const cadence = getRecurringCadenceLabel(row.original)
            // Only first-run recurring reports show the info tooltip — the column shows "First run …"
            // and the tooltip reveals the actual cadence (e.g. "Runs Daily 12AM").
            const showCadenceTooltip = schedule.startsWith('First run') && !!cadence
            return (
              <div className='flex min-h-[48px] items-center gap-2 whitespace-nowrap pl-2 pr-3'>
                <ScheduleText schedule={schedule} />
                {showCadenceTooltip && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='inline-flex shrink-0' onClick={e => e.stopPropagation()}>
                        <Icon IconComponent={Info} size='xs' className='text-zinc-400' iconProps={{ weight: 'duotone' }} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent align='start'>{cadence}</TooltipContent>
                  </Tooltip>
                )}
              </div>
            )
          }
        },
        {
          header: 'Last generated',
          id: 'last_generated',
          minSize: 80,
          maxSize: 80,
          cell: ({ row }) => {
            const tooltip = formatLastGeneratedTooltip(row.original)
            const content = (
              <div className='flex min-h-[48px] items-center gap-1 px-2 text-text-xs-regular text-zinc-500'>
                <Icon IconComponent={ClockCounterClockwise} size='xxs' className='shrink-0 text-zinc-400' />
                {formatLastGenerated(row.original)}
              </div>
            )
            return tooltip ? (
              <Tooltip>
                <TooltipTrigger asChild>{content}</TooltipTrigger>
                <TooltipContent align='start'>{tooltip}</TooltipContent>
              </Tooltip>
            ) : (
              content
            )
          }
        },
        {
          header: '',
          id: 'actions',
          minSize: 90,
          cell: ({ row }) => (
            <ReportRowActions
              report={row.original}
              onPauseClick={onPauseClick}
              onResumeClick={onResumeClick}
              onEdit={onEdit}
              onSetupRecurring={onSetupRecurring}
              mutateReports={mutateReports}
            />
          )
        }
      ],
      [t, openHistory, onPauseClick, onResumeClick, mutateReports, onEdit, onSetupRecurring]
    )

    return (
      <div className='w-full'>
        <GenericTableV2
          data={report}
          columns={columns}
          scrollMode={ScrollMode.INFINITE}
          isLoading={isLoading}
          roundedRows
          getRowId={row => row.reportId}
          highlightedRowId={highlightedReportId}
          infiniteScroll={{
            hasMore: hasMore ?? false,
            onLoadMore: onLoadMore ?? (() => { }),
            isLoading: isLoadingMore ?? false
          }}
          onRowClick={row => {
            if (row.frequencyType === 'RECURRING') openHistory(row)
          }}
          customizationConfig={{ header: { hideHeader: true } }}
        />
        {selectedReport && (
          <MultipleReportDialog
            open={openHistoryDialog}
            onClose={() => {
              setOpenHistoryDialog(false)
              // The history modal fetched fresh runs — sync the list row's count / last-generated.
              mutateReports?.()
            }}
            report={selectedReport}
          />
        )}
      </div>
    )
  }

export default ReportList
